import { useCallback, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { CellStyle, ColDef } from 'ag-grid-community';
import type { ICellEditorParams, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import ScenarioDateCellEditor from './ScenarioDateCellEditor';
import { DraftIssue, ScenarioDraftRow, scenarioRowAnchorId } from './scenarioDraft';
import {
    formatBudgetDate,
    generateMonthColumns,
    MONTH_KEY,
    parseBudgetCell,
    SCENARIO_GRID_CLASS,
    SCENARIO_HEADER_HEIGHT,
    SCENARIO_ROW_HEIGHT,
    SCENARIO_ROW_MONTH_COL_WIDTH,
    scenarioFillerColumn,
} from './helpers';

/** A dropdown choice of a `select` column. `id` is what the cell stores. */
export interface ScenarioRowOption {
    id: number;
    label: string;
}

/** Shared by every column kind: what the header says and how wide the column is. */
interface ScenarioRowColumnBase {
    headerName: string;
    /** A `width`, not a minWidth: these columns are flex:0 (see buildColumn), and on a flex:0 column
     *  ag-grid sizes by `width` — whose default is 200px. Declaring only a minWidth is what made every
     *  column render at 200 regardless of what it asked for. The wrapper's minWidth:70 still applies, so a
     *  user can drag one narrower than this.
     *
     *  Sized for the CELL, not for the header: headers truncate with an ellipsis and carry a tooltip since
     *  CALL-784, so a long caption no longer buys the whole column 40 extra pixels. */
    width: number;
    /** Only the identity column pins — it is what a row is read by while the months scroll. */
    pinned?: 'left';
}

/** One column of a row grid.
 *
 *  `key` is a real field of the form row, not a free string: before the CALL-783 extraction each input
 *  called `patchRow(clientKey, { trainerCost: … })` and was checked against the row type, so a typo was
 *  a compile error. Keyed as `keyof TForm & string`, a mistyped `trainerCst` stays one — otherwise it
 *  would render a permanently empty column, write a junk property, and drop the entered value on save
 *  with no error anywhere. `computed` columns are the exception: they hold no field at all, so their
 *  `field` is a plain grid-only name. */
export type ScenarioRowColumn<TForm> =
    | (ScenarioRowColumnBase & {
          kind: 'select';
          key: keyof TForm & string;
          /** A function where the choices depend on the row (Driver excludes worktypes other rows use). */
          options: ScenarioRowOption[] | ((row: TForm) => ScenarioRowOption[]);
      })
    | (ScenarioRowColumnBase & {
          kind: 'number';
          key: keyof TForm & string;
          format?: (value: unknown) => string;
      })
    | (ScenarioRowColumnBase & { kind: 'date'; key: keyof TForm & string })
    | (ScenarioRowColumnBase & {
          kind: 'computed';
          field: string;
          compute: (row: TForm) => number | string | null;
          format?: (value: unknown) => string;
      });

export interface ScenarioRowGridProps<TForm extends ScenarioDraftRow> {
    months: string[];
    rows: TForm[];
    /** The leading (non-month) columns, in board order. */
    columns: ScenarioRowColumn<TForm>[];
    isLoading: boolean;
    canEdit: boolean;
    /** Blocking problems for the whole draft; the alert above the grid picks out the ones naming a row. */
    issues: DraftIssue[];
    /** Monthly cell rendering. */
    formatMonthValue: (value: unknown) => string;
    /** Monthly cell parsing. Defaults to the shared German-number parse (blank → null); Contacts overrides
     *  it to round, because a fractional contact is not storable. */
    parseMonthValue?: (rawValue: unknown) => number | null;
    /** How a flagged row is named in the warning alert. Falsy → the row's position. */
    rowLabel: (row: TForm) => string;
    onFieldChange: (clientKey: string, key: keyof TForm & string, value: number | string) => void;
    onMonthValueChange: (clientKey: string, month: string, value: number | null) => void;
    /** Omitted where the scope multiselect is the only way to add a row (Driver, Kontakte: one row per
     *  worktype). Where it IS given, the grid only offers it inside its EMPTY state — the persistent button
     *  lives in the section heading, which is the one row both views of the section have (CALL-784). */
    onAddRow?: () => void;
    onRemoveRow: (row: TForm) => void;
    deleteConfirmOpen: boolean;
    onCancelDelete: () => void;
    onConfirmDelete: () => void;
    labels: {
        /** Only needed alongside `onAddRow` — the button's caption. */
        addRow?: string;
        noRows: string;
        /** Used for the delete button title, its aria-label AND the confirmation dialog title. */
        deleteRow: string;
    };
}

/** The pinned trash column. Just wide enough for a 30px IconButton — see the colDef below. */
const ACTION_COL_WIDTH = 44;

const EDITABLE_CELL_STYLE: CellStyle = { backgroundColor: '#fffde7' };
const NUMBER_CELL_STYLE: CellStyle = { ...EDITABLE_CELL_STYLE, textAlign: 'right' };
const READ_ONLY_NUMBER_CELL_STYLE: CellStyle = { textAlign: 'right' };

/** A cell's displayed text — what its valueFormatter would render. Every column feeds it to
 *  `tooltipValueGetter` as well (CALL-784): the select columns already did, and the VALUES clip just as
 *  readily as the captions do. "15.09.2026" needs 74px and Trainingsbeginn is 75px wide, so the two date
 *  columns rendered "15.09.20…" with no way to read the rest — the cell editor is not one, since it replaces
 *  the text with a picker. Cheaper than deciding per column which formats can overflow their width. */
const formatCell = (format: ((value: unknown) => string) | undefined, value: unknown): string =>
    format ? format(value) : String(value ?? '');

// The inline row grid shared by every input type that is a row of scalars plus a monthly series —
// Verschiebung, GA/CB/VVL and, since its training block, Kontakte (CALL-783). Shift and driver used to be
// lists of outlined cards with a per-row SPEICHERN; the board shows ONE editable table per type, which is
// also the only shape that stays readable past a handful of rows.
//
// A type contributes nothing but a `columns` array and its edit handlers: every mechanical concern —
// dropdown editors, German number parsing, the month block, the trash, the validation alert — lives here,
// so the three types can no longer drift apart the way the card wiring did.
//
// There is no per-row save: an edit reports a new draft upward and the page's single Save commits the lot.
// Neither the title nor any prose lives here: the section header owns both the title (which doubles as the
// input⇄impact toggle) and the one help icon whose tooltip carries the formula AND the note about the
// calculated columns — on screen those three lines pushed the table itself out of the first viewport.
const ScenarioRowGrid = <TForm extends ScenarioDraftRow>({
    months,
    rows,
    columns,
    isLoading,
    canEdit,
    issues,
    formatMonthValue,
    parseMonthValue,
    rowLabel,
    onFieldChange,
    onMonthValueChange,
    onAddRow,
    onRemoveRow,
    deleteConfirmOpen,
    onCancelDelete,
    onConfirmDelete,
    labels,
}: ScenarioRowGridProps<TForm>) => {
    const { t } = useTranslation('budget');

    // ag-grid addresses month cells by a 'YYYY-MM-DD' field, so the row's nested monthValues are spread
    // flat beside its scalar fields. Column keys are identifiers, so the two can never collide — and a
    // `computed` valueGetter still receives the whole row.
    const rowData = useMemo(() => rows.map((row) => ({ ...row, ...row.monthValues })), [rows]);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(months, {
                // BOTH, and both deliberately: the helper hard-codes `minWidth: 110` for the shared
                // budget grids, and a minWidth above the width simply wins — declaring only the width
                // left these columns rendering at 110 while every test read 80 (CALL-784).
                width: SCENARIO_ROW_MONTH_COL_WIDTH,
                minWidth: SCENARIO_ROW_MONTH_COL_WIDTH,
                editable: canEdit,
                valueFormatter: (params) => formatMonthValue(params.value),
                valueParser: (params) => {
                    if (params.newValue === '' || params.newValue == null) return null;
                    return parseMonthValue ? parseMonthValue(params.newValue) : parseBudgetCell(params.newValue);
                },
                cellStyle: (): CellStyle => (canEdit ? NUMBER_CELL_STYLE : READ_ONLY_NUMBER_CELL_STYLE),
            }),
        [months, canEdit, formatMonthValue, parseMonthValue]
    );

    // A named builder rather than an inline map callback: the switch is exhaustive over the union, which
    // TypeScript checks but ESLint's array-callback-return cannot see.
    const leadingColumns = useMemo<ColDef[]>(() => {
        const buildColumn = (column: ScenarioRowColumn<TForm>): ColDef => {
            const base: ColDef = {
                headerName: column.headerName,
                // The full caption, for the header that now truncates it (CALL-784). Nothing is lost to the
                // ellipsis, and it costs one property rather than a per-column decision about which
                // captions are safe to shorten.
                headerTooltip: column.headerName,
                // A width rather than a minWidth: with flex:0 below, ag-grid sizes the column by `width`
                // and falls back to its 200px default when none is given — which is what made a 100px
                // Agents column as wide as a 260px Working Unit one.
                width: column.width,
                // The shared wrapper defaults every column to flex:1 while generateMonthColumns sets
                // flex:0. Mixing the two makes this leading block stretch while the months do not, and
                // the grid visibly wobbles on every window resize.
                flex: 0,
                ...(column.pinned ? { pinned: column.pinned } : {}),
            };
            switch (column.kind) {
                case 'select': {
                    const optionsFor = (row: TForm | undefined): ScenarioRowOption[] =>
                        typeof column.options === 'function' ? (row ? column.options(row) : []) : column.options;
                    const labelFor = (row: TForm | undefined, value: unknown): string =>
                        optionsFor(row).find((option) => option.id === value)?.label ?? '';
                    return {
                        ...base,
                        field: column.key,
                        editable: canEdit,
                        ...(canEdit ? { cellStyle: EDITABLE_CELL_STYLE } : {}),
                        valueFormatter: (params: ValueFormatterParams) =>
                            labelFor(params.data as TForm | undefined, params.value),
                        // The select columns hold the only free text in these grids — a working unit or a
                        // partner–location is the longest string on the screen. Since CALL-783 they WRAP over
                        // the two lines SCENARIO_ROW_HEIGHT provides, the way the board's own captions do,
                        // instead of truncating at one; the column widths are then set by the longest single
                        // WORD each must not break (see SCENARIO_ROW_LABEL_COL_WIDTH).
                        // Not `autoHeight`: that sizes each row to its own content, and rows of differing
                        // heights are what one fixed row height (and the inline-editor height keyed to it)
                        // exists to prevent. Overflow past the row is clipped by the row itself.
                        wrapText: true,
                        // A label too long even for two lines still clips (CALL-784), so the full name has to
                        // stay one hover away — the cell editor is not a substitute, it replaces the text
                        // with a picker.
                        tooltipValueGetter: (params) => labelFor(params.data as TForm | undefined, params.value),
                        // Declared rather than inferred, for the same reason as the date column below —
                        // and here it is not cosmetic but the difference between a usable type and an
                        // unusable one. Left to infer, ag-grid types a column from the FIRST row's value:
                        // Von/Nach start as '' on every row, so the column came out 'text', and
                        // valueService.setValue then rejects the numeric option id outright
                        // ("Data type of the new value does not match the cell data type of the column")
                        // and returns without firing cellValueChanged. The pick simply evaporated — the
                        // cell stayed blank, "Von und Nach sind erforderlich" never cleared and Save
                        // stayed disabled for the whole form. Working Unit escaped it only by accident:
                        // the multiselect seeds row 1 with a real id, so THAT column inferred 'number'.
                        //
                        // The option ids are numbers, so 'number' is the honest type. A cleared cell is
                        // still '': ag-grid deletes through getDeleteValue → null (checkType lets null
                        // past whatever the column's type is), and onCellValueChanged maps null back to
                        // the empty string the draft holds — so the row re-raises its own validation
                        // instead of silently reading as location #0.
                        cellDataType: 'number',
                        cellEditor: 'agRichSelectCellEditor',
                        // As a POPUP, not inline. agRichSelectCellEditor renders inside the cell by
                        // default, so its option list is clipped by the grid viewport's overflow —
                        // invisible in these grids, which are 1-5 rows tall with domLayout="autoHeight"
                        // and whose App.css rules deliberately leave no slack under the last row. The
                        // shared wrapper sets popupParent={document.body}, so a popup editor escapes the
                        // grid entirely. The date column below is a popup for the same reason; only the
                        // number and month cells stay inline, being single inputs that fit a 32px row.
                        cellEditorPopup: true,
                        cellEditorPopupPosition: 'under',
                        // A function of params, not a literal: Driver's choices depend on what the
                        // OTHER rows already use, so they have to be resolved per row being edited.
                        cellEditorParams: (params: ICellEditorParams) => {
                            const options = optionsFor(params.data as TForm | undefined);
                            return {
                                values: options.map((option) => option.id),
                                formatValue: (value: number) =>
                                    options.find((option) => option.id === value)?.label ?? '',
                            };
                        },
                    };
                }
                case 'number':
                    return {
                        ...base,
                        field: column.key,
                        editable: canEdit,
                        cellStyle: canEdit ? NUMBER_CELL_STYLE : READ_ONLY_NUMBER_CELL_STYLE,
                        valueFormatter: (params: ValueFormatterParams) =>
                            column.format ? column.format(params.value) : String(params.value ?? ''),
                        tooltipValueGetter: (params) => formatCell(column.format, params.value),
                        valueParser: (params) =>
                            params.newValue === '' || params.newValue == null ? null : parseBudgetCell(params.newValue),
                    };
                case 'date':
                    return {
                        ...base,
                        field: column.key,
                        editable: canEdit,
                        ...(canEdit ? { cellStyle: EDITABLE_CELL_STYLE } : {}),
                        // ISO 'YYYY-MM-DD' in, ISO out — our DATEONLY wire format, so the cell needs no
                        // conversion. formatBudgetDate is the one date formatter that does not apply the
                        // UTC→Berlin shift, which would move a date-only value across a day.
                        //
                        // cellDataType is declared rather than inferred: ag-grid types a string field as
                        // 'text', and the date editor's parse/format contract only holds for 'dateString'.
                        // (The select columns above need the same declaration for a harsher reason.) It
                        // also gates the commit: on a 'dateString' column checkType passes null and
                        // 'YYYY-MM-DD' and rejects everything else, and a rejected value never fires
                        // cellValueChanged — which is why the editor reports an empty field as null.
                        cellDataType: 'dateString',
                        // OURS, not ag-grid's stock agDateStringCellEditor: that one is a native
                        // input[type=date] and renders in the BROWSER/OS locale, so the open editor read
                        // "09/15/2026" on an en-US machine while the cell beside it read "15.09.2026"
                        // (CALL-794). It took no typed text either. See the component for the rest.
                        cellEditor: ScenarioDateCellEditor,
                        // A POPUP, like the dropdowns and for the same underlying reason — and it stays one
                        // now that the editor is a MUI field: any date control is taller than a row. The
                        // native one Chrome renders 40px tall grew the CELL to 72px inside a 32px row
                        // without growing the row, so it overlaid the cells of the row below and its own
                        // bottom border was cut off by the grid's horizontal scrollbar. The App.css rule
                        // that was meant to hold inline editors to the row height cannot help here: it is
                        // `.budget-grid .ag-cell-inline-editing`, exactly as specific as the material
                        // theme's own `.ag-theme-material .ag-cell-inline-editing`, which is loaded after
                        // App.css and therefore wins (the rich-select rule beside it only works because
                        // its :has() adds a class's worth of specificity). So the editor gets its own
                        // layer instead, which App.css raises (`body .ag-popup-child`) and makes opaque
                        // (`body .ag-popup-editor`) — both rules were written for THIS editor.
                        cellEditorPopup: true,
                        cellEditorPopupPosition: 'under',
                        valueFormatter: (params: ValueFormatterParams) => formatBudgetDate(params.value),
                        tooltipValueGetter: (params) => formatBudgetDate(params.value),
                    };
                case 'computed':
                    // Deliberately NOT yellow: in this repo the yellow background means "you type
                    // here", and these two columns are arithmetic the server repeats.
                    return {
                        ...base,
                        field: column.field,
                        editable: false,
                        cellStyle: READ_ONLY_NUMBER_CELL_STYLE,
                        valueGetter: (params) => (params.data ? column.compute(params.data as TForm) : null),
                        valueFormatter: (params: ValueFormatterParams) =>
                            column.format ? column.format(params.value) : String(params.value ?? ''),
                        tooltipValueGetter: (params) => formatCell(column.format, params.value),
                    };
            }
        };
        return columns.map(buildColumn);
    }, [columns, canEdit]);

    const columnDefs = useMemo<ColDef[]>(() => {
        const trash: ColDef[] = canEdit
            ? [
                  {
                      headerName: '',
                      colId: 'actions',
                      // 44, not 60: a small IconButton is 30px, and pinned columns are excluded from
                      // ag-grid's flex distribution — so every pixel here is one the month block on the
                      // other side of the grid cannot have (CALL-784).
                      width: ACTION_COL_WIDTH,
                      minWidth: ACTION_COL_WIDTH,
                      maxWidth: ACTION_COL_WIDTH,
                      flex: 0,
                      pinned: 'right',
                      sortable: false,
                      filter: false,
                      editable: false,
                      cellRenderer: (params: ICellRendererParams) =>
                          params.data ? (
                              <IconButton
                                  size="small"
                                  onClick={() => onRemoveRow(params.data as TForm)}
                                  title={labels.deleteRow}
                                  aria-label={labels.deleteRow}
                              >
                                  <DeleteIcon fontSize="small" />
                              </IconButton>
                          ) : null,
                  },
              ]
            : [];
        // The months are plain siblings of the leading columns, NOT a column group: ag-grid draws a group
        // header row across the whole grid, so a caption over the month block gave every other column an
        // empty cell above it — a double-height header, worst of all on an empty grid, and the reason the
        // page jumped vertically on every input⇄impact toggle (the impact grid has no group). The month
        // headers already read "Aug. 26", "Sept. 26" …, so the caption only restated them.
        // The filler sits between the months and the pinned trash: it soaks up the width a short period
        // leaves over, so the table fills its own shell instead of ending halfway across it (CALL-784).
        return [...leadingColumns, ...monthColumns, scenarioFillerColumn(), ...trash];
    }, [leadingColumns, monthColumns, canEdit, onRemoveRow, labels.deleteRow]);

    // filter:false everywhere → the header menu (kebab) would be empty noise; hide it. Not sortable
    // either: rows are added at the bottom and identified by an editable cell, so a sort would move the
    // row under the cursor.
    const defaultColDef = useMemo<ColDef>(
        () => ({ sortable: false, filter: false, resizable: true, suppressMenu: true }),
        []
    );

    const headerByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const column of columns) {
            if (column.kind !== 'computed') map.set(column.key, column.headerName);
        }
        return map;
    }, [columns]);

    const onCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const field = event.colDef.field;
            const clientKey = event.data?.clientKey;
            if (!field || !clientKey) return;
            if (MONTH_KEY.test(field)) {
                onMonthValueChange(clientKey, field, event.newValue ?? null);
                return;
            }
            // A cleared cell becomes '' rather than null: that is what the draft's numeric fields hold
            // while half-typed, and what coerces back to 0 on save.
            onFieldChange(clientKey, field as keyof TForm & string, event.newValue ?? '');
        },
        [onFieldChange, onMonthValueChange]
    );

    // A grid has no per-row helper-text slot, so the draft's problems for these rows are collected into one
    // alert that names each offending row.
    //
    // The column caption is prefixed only where the issue asks for it with a `field` — i.e. where its message
    // cannot name the field itself ("Darf nicht negativ sein"). Messages that do name it carry no `field` and
    // are printed verbatim, which is what stopped "Working Unit: Working Unit ist erforderlich" (CALL-784).
    // The caption comes from the colDef the user is looking at rather than from the validator, so a prefixed
    // message always names the column as that column is labelled.
    const flaggedRows = useMemo(
        () =>
            rows
                .map((row, index) => ({
                    row,
                    index,
                    messages: issues
                        .filter((issue) => issue.clientKey === row.clientKey)
                        .map((issue) => {
                            const header = issue.field ? headerByKey.get(issue.field) : undefined;
                            return header ? `${header}: ${issue.message}` : issue.message;
                        }),
                }))
                .filter((entry) => entry.messages.length > 0),
        [rows, issues, headerByKey]
    );

    // An empty grid CARRIES its action rather than describing it (CALL-784): the old copy told the user to
    // press "Verschiebung hinzufügen", a button that sat 60px above it and right-aligned, in a 165px band of
    // nothing. Where a type has no add button (Driver, Kontakte) the sentence is all there is — the rows
    // there come from the form's multiselect, which the copy names.
    const noRowsOverlay = () => (
        <Stack alignItems="center" spacing={1} sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
                {labels.noRows}
            </Typography>
            {canEdit && onAddRow && (
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAddRow}>
                    <span>{labels.addRow}</span>
                </Button>
            )}
        </Stack>
    );

    return (
        // App.css scopes the rules that make the rich-select editor and the inline editor fit a 32px row,
        // and the ones that truncate the headers, to these classes.
        <Box className={SCENARIO_GRID_CLASS}>
            {flaggedRows.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {flaggedRows.map((entry) => (
                        // The anchor "Go to error" scrolls to. Grid rows are virtualised, so this line is
                        // the one element that reliably exists for a flagged row.
                        <Box key={entry.row.clientKey} id={scenarioRowAnchorId(entry.row.clientKey)}>
                            <span className="notranslate">
                                {`${rowLabel(entry.row) || `#${entry.index + 1}`} — ${entry.messages.join('; ')}`}
                            </span>
                        </Box>
                    ))}
                </Alert>
            )}

            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                headerHeight={SCENARIO_HEADER_HEIGHT}
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.clientKey)}
                loading={isLoading}
                enableRangeSelection
                // ag-grid's default (false) leaves an editor that is still open when focus leaves the grid
                // neither committed nor closed, so cellValueChanged never fires and the draft never learns
                // the typed value. Beside another change it is dropped by a Save that reports success;
                // ALONE it never makes the form dirty at all, so SPEICHERN stays disabled and the
                // unsaved-changes guard — the one mechanism meant to stop work disappearing — has nothing
                // to warn about either.
                //
                // Deliberately set here and not on the shared AgGrid wrapper: the ~16 other grids behind it
                // save INSIDE onCellValueChanged (BillingConfigGrid, OnTopCostsGrid, useRateGrid, …), so
                // there this would give every one of them a blur-commits-then-PATCHes network path it has
                // never had. The three scenario input grids only mutate a local draft, so for them
                // committing on blur is free.
                stopEditingWhenCellsLoseFocus
                onCellValueChanged={onCellValueChanged}
                noRowsOverlayComponent={noRowsOverlay}
            />

            <Dialog open={deleteConfirmOpen} onClose={onCancelDelete} maxWidth="sm">
                <DialogTitle>{labels.deleteRow}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('dialog.deleteScenarioInputRow')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelDelete} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    <Button onClick={onConfirmDelete} variant="contained" color="error">
                        {t('action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ScenarioRowGrid;
